import { useEffect, useMemo, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../../lib/trpc';
import { useMessengerRealtime } from '../../../lib/realtime';
import { THREAD_PAGE_LIMIT, flattenThread } from '../../../lib/thread-cache';
import { messagingWindowState } from '../../../lib/chat-format';
import type {
  InboxAdapter,
  InboxListItem,
  InboxMessage,
  InboxThreadResult,
  InboxWindowState,
} from '../types';

/** Persisted "which Page am I viewing" key — survives reloads so the inbox
 *  reopens on the same Page (validated against the live list before use). */
const PAGE_SCOPE_KEY = 'leadsync.messenger.activePageId';

function msTime(d: string | Date | null | undefined): number {
  return d ? new Date(d).getTime() : 0;
}

/** Structural view of an enriched Messenger conversation row (router output). */
interface FbConvo {
  id: number;
  facebookPageId: number;
  psid: string;
  profileName: string | null;
  profilePictureUrl: string | null;
  contactName: string | null;
  contactUid: string | null;
  contactId: number | null;
  unreadCount: number;
  lastMessage: string | null;
  lastMessageAt: string | Date | null;
  lastIncomingAt: string | Date | null;
}

function initial(name: string): string {
  const t = name.trim();
  return t ? t[0]!.toUpperCase() : '?';
}

function toListItem(c: FbConvo): InboxListItem {
  const title = c.profileName ?? c.contactName ?? c.psid;
  return {
    id: c.id,
    title,
    subtitle: c.psid,
    lastMessage: c.lastMessage,
    avatarUrl: c.profilePictureUrl,
    avatarInitial: initial(title),
    lastMessageAt: c.lastMessageAt,
    lastIncomingAt: c.lastIncomingAt,
    unreadCount: c.unreadCount,
    contactId: c.contactId,
  };
}

function toWindow(lastIncomingAt: string | Date | null): InboxWindowState {
  const w = messagingWindowState(lastIncomingAt ? new Date(lastIncomingAt) : null);
  return { canFreeText: w.kind !== 'closed', variant: 'badge', label: w.label, color: w.color };
}

/** Messenger ad/referral payload (Click-to-Messenger / m.me), stored as JSON. */
function parseReferral(
  json: string | null | undefined,
): { ref?: string; ads_context_data?: { ad_title?: string; photo_url?: string } } | null {
  if (!json) return null;
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/** Short preview of a quoted message for the reply strip. */
function replyPreview(r: { message: string | null; messageType: string | null }): string {
  return (r.message?.trim() || `[${r.messageType ?? 'message'}]`).slice(0, 120);
}

function useMessengerThread(selectionId: number, convo: FbConvo | undefined): InboxThreadResult {
  // Keyset infinite query — newest page first, older pages lazy-loaded on
  // scroll-up by the thread's IntersectionObserver sentinel.
  const messages = trpc.facebook.inbox.messages.useInfiniteQuery(
    { conversationId: selectionId, limit: THREAD_PAGE_LIMIT },
    { getNextPageParam: (last) => last.nextCursor ?? undefined },
  );
  const title = convo ? (convo.profileName ?? convo.contactName ?? convo.psid) : '';
  const rows = useMemo(() => flattenThread(messages.data), [messages.data]);
  // Index by Meta mid so a reply can resolve its quoted message client-side.
  const byMid = new Map<string, (typeof rows)[number]>();
  for (const r of rows) if (r.externalMessageId) byMid.set(r.externalMessageId, r);
  return {
    header: convo
      ? {
          title,
          subtitle: convo.psid,
          avatarUrl: convo.profilePictureUrl,
          avatarInitial: initial(title),
          contactUid: convo.contactUid,
        }
      : null,
    messages: rows.map((m): InboxMessage => {
      const quoted = m.reactedToMessageId ? byMid.get(m.reactedToMessageId) : undefined;
      const referral = parseReferral(m.referralJson);
      return {
        id: m.id,
        isIncoming: m.isIncomingMessage === 1,
        text: m.message,
        messageType: m.messageType,
        status: m.status,
        messagedAt: m.messagedAt,
        payloadJson: m.payloadJson,
        mediaUrl: m.mediaUrl,
        mimeType: m.mimeType,
        stickerId: m.stickerId,
        latitude: m.latitude,
        longitude: m.longitude,
        replyTo: quoted
          ? {
              id: quoted.id,
              preview: replyPreview(quoted),
              isIncoming: quoted.isIncomingMessage === 1,
            }
          : null,
        referralHeadline: referral?.ads_context_data?.ad_title ?? referral?.ref ?? null,
        referralSourceUrl: referral?.ads_context_data?.photo_url ?? null,
      };
    }),
    windowState: convo ? toWindow(convo.lastIncomingAt) : { canFreeText: true, variant: 'none' },
    isPending: messages.isPending,
    hasMore: messages.hasNextPage,
    loadOlder: () => void messages.fetchNextPage(),
    isFetchingOlder: messages.isFetchingNextPage,
  };
}

/** Messenger data adapter — wraps `facebook.inbox.*`, hides Conversation.id /
 *  (pageId, psid) addressing, drives realtime, and exposes the feature-rich
 *  send actions (media, templates, typing). */
export function useMessengerInbox(): InboxAdapter {
  const utils = trpc.useUtils();

  // Connected Pages for the switcher, each with its unread total + last
  // activity. Polled so the per-Page unread badge stays fresh.
  const pageScopes = trpc.facebook.inbox.pages.useQuery(undefined, {
    refetchInterval: 30_000,
    refetchIntervalInBackground: true,
  });
  const pages = pageScopes.data ?? [];

  // "One Page at a time": the Page currently being viewed. Resolved once the
  // Pages load — persisted choice → most-active Page → first connected.
  const [activePageId, setActivePageId] = useState<number | null>(null);
  useEffect(() => {
    if (pages.length === 0) return;
    if (activePageId != null && pages.some((p) => p.id === activePageId)) return;
    const stored = Number(localStorage.getItem(PAGE_SCOPE_KEY));
    const persisted = pages.find((p) => p.id === stored);
    const mostActive = [...pages].sort(
      (a, b) => msTime(b.lastMessageAt) - msTime(a.lastMessageAt),
    )[0];
    setActivePageId(persisted?.id ?? mostActive?.id ?? pages[0]!.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageScopes.data]);

  const selectPage = (value: string) => {
    const id = Number(value);
    if (!Number.isFinite(id)) return;
    localStorage.setItem(PAGE_SCOPE_KEY, String(id));
    setActivePageId(id);
  };

  const conversations = trpc.facebook.inbox.conversations.useQuery(
    { page: 1, pageSize: 100, pageId: activePageId ?? undefined },
    {
      // Hold the list until we know the Pages: run unfiltered only when there
      // are none, otherwise once a Page is selected — avoids a flash of every
      // Page's chats before the default Page resolves.
      enabled: !pageScopes.isPending && (pages.length === 0 || activePageId != null),
      refetchInterval: 30_000,
      refetchIntervalInBackground: true,
    },
  );
  const onErr = (err: { message: string }) =>
    notifications.show({ message: err.message, color: 'red' });
  const sendText = trpc.facebook.inbox.sendText.useMutation({ onError: onErr });
  const sendMediaUpload = trpc.facebook.inbox.sendMediaUpload.useMutation({ onError: onErr });
  const sendMediaFromLibrary = trpc.facebook.inbox.sendMediaFromLibrary.useMutation({
    onError: onErr,
  });
  const sendQuickReplies = trpc.facebook.inbox.sendQuickReplies.useMutation({ onError: onErr });
  const sendGenericTemplate = trpc.facebook.inbox.sendGenericTemplate.useMutation({
    onError: onErr,
  });
  const sendButtonTemplate = trpc.facebook.inbox.sendButtonTemplate.useMutation({ onError: onErr });
  const sendMediaTemplate = trpc.facebook.inbox.sendMediaTemplate.useMutation({ onError: onErr });
  const sendReceiptTemplate = trpc.facebook.inbox.sendReceiptTemplate.useMutation({
    onError: onErr,
  });
  const sendTyping = trpc.facebook.inbox.sendTypingIndicator.useMutation();
  const markAsRead = trpc.facebook.inbox.markAsRead.useMutation({
    // Clear the sidebar unread badge + nav unread total + the Page-switcher's
    // per-Page unread the instant a thread is opened; without this onSuccess
    // they only refresh on the 30s poll / reload.
    onSuccess: () => {
      void utils.facebook.inbox.conversations.invalidate();
      void utils.facebook.inbox.totalUnread.invalidate();
      void utils.facebook.inbox.pages.invalidate();
    },
  });
  const refreshProfile = trpc.facebook.inbox.refreshProfile.useMutation({
    onSuccess: (res) => {
      void utils.facebook.inbox.conversations.invalidate();
      if (res.ok === false) {
        // Surface the exact reason Meta gave so the missing name is debuggable
        // (token expired vs. permission/Advanced-Access vs. unresolvable PSID).
        const parts: string[] = [res.message ?? 'No name available for this user.'];
        if (res.code) parts.push(`(#${res.code}${res.subcode ? `/${res.subcode}` : ''})`);
        if (res.tokenOk === false)
          parts.push('— the Page token looks invalid; reconnect the Page.');
        notifications.show({
          title: 'Name unavailable',
          message: parts.join(' '),
          color: 'yellow',
          autoClose: 9000,
        });
      }
    },
    onError: onErr,
  });

  const items: FbConvo[] = conversations.data?.items ?? [];
  const byId = new Map(items.map((c) => [c.id, c]));

  // Page switcher fed to the shell. Present whenever ≥1 Page is connected; the
  // ConversationList shows a dropdown for ≥2 and a static caption for exactly 1.
  const listScope: InboxAdapter['listScope'] =
    pages.length >= 1
      ? {
          value: activePageId != null ? String(activePageId) : '',
          onChange: selectPage,
          options: pages.map((p) => ({
            value: String(p.id),
            label: p.pageName,
            avatarUrl: p.profilePictureUrl,
            unreadCount: p.unreadCount,
          })),
        }
      : undefined;

  return {
    channel: 'messenger',
    useRealtime: useMessengerRealtime,
    useList: () => ({
      items: items.map(toListItem),
      isPending: conversations.isPending,
      isError: conversations.isError,
      error: conversations.error ? { message: conversations.error.message } : null,
    }),
    useThread: (selectionId) =>
      useMessengerThread(selectionId ?? 0, selectionId != null ? byId.get(selectionId) : undefined),
    actions: {
      sendText: (selectionId, text) => {
        const c = byId.get(selectionId);
        if (c) sendText.mutate({ pageId: c.facebookPageId, psid: c.psid, text });
      },
      sendMedia: (selectionId, file) => {
        const c = byId.get(selectionId);
        if (!c) return;
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = (reader.result as string).split(',')[1];
          if (!base64) return;
          sendMediaUpload.mutate({
            pageId: c.facebookPageId,
            psid: c.psid,
            bytesBase64: base64,
            mimeType: file.type || 'application/octet-stream',
            filename: file.name,
          });
        };
        reader.readAsDataURL(file);
      },
      sendMediaFromLibrary: (selectionId, mediaAssetId) => {
        const c = byId.get(selectionId);
        if (c)
          sendMediaFromLibrary.mutate({ pageId: c.facebookPageId, psid: c.psid, mediaAssetId });
      },
      sendTemplate: (selectionId, tpl) => {
        const c = byId.get(selectionId);
        if (!c) return;
        const base = { pageId: c.facebookPageId, psid: c.psid };
        const p = tpl.payload as Record<string, unknown>;
        switch (tpl.kind) {
          case 'quick_replies':
            sendQuickReplies.mutate({ ...base, ...(p as { text: string; quickReplies: never[] }) });
            break;
          case 'generic':
            sendGenericTemplate.mutate({
              ...base,
              elements: (p as { elements: never[] }).elements,
            });
            break;
          case 'button':
            sendButtonTemplate.mutate({ ...base, ...(p as { text: string; buttons: never[] }) });
            break;
          case 'media':
            sendMediaTemplate.mutate({
              ...base,
              ...(p as { mediaType: 'image' | 'video'; url: string }),
            });
            break;
          case 'receipt':
            sendReceiptTemplate.mutate({
              ...base,
              ...(p as {
                recipientName: string;
                orderNumber: string;
                currency: string;
                paymentMethod: string;
                summary: { totalCost: number };
                elements: never[];
              }),
            });
            break;
        }
      },
      markAsRead: (selectionId) => markAsRead.mutate({ conversationId: selectionId }),
      sendTyping: (selectionId) => sendTyping.mutate({ conversationId: selectionId }),
      refreshProfile: (selectionId) => refreshProfile.mutate({ conversationId: selectionId }),
    },
    sendingText: sendText.isPending,
    sendingMedia: sendMediaUpload.isPending,
    listScope,
  };
}
